// ────────────────────────────────────────────────────────────────────
// SitePlot2D — 2D plot-plan overlay with health-coded asset markers.
//
// Fetches the plot-plan PNG via authenticated axios (same pattern as
// AssetStlViewer / assetPhotoUrl — a plain <img src=...> cannot send the
// Bearer JWT, so we build a blob object URL from the ArrayBuffer).
//
// Markers are absolutely positioned buttons at the asset's metres→% position
// (toPlotPct from sitePlot.ts). Clicking a marker calls onPick(assetId).
// ────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import type { Asset, Area } from "@/lib/types";
import { apiClient } from "@/lib/api";
import { toPlotPct, healthColor } from "@/lib/sitePlot";

interface Props {
  area: Area;
  assets: Asset[];
  onPick: (id: number) => void;
}

export default function SitePlot2D({ area, assets, onPick }: Props): JSX.Element {
  const w = Number(area.plot_plan_width_m) || 0;
  const h = Number(area.plot_plan_height_m) || 0;
  const [src, setSrc] = useState<string>("");
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    // Reset when area changes.
    setSrc("");
    setImgError(false);
    let objectUrl = "";

    apiClient
      .areaPlotPlan(area.id)
      .then((buf) => {
        objectUrl = URL.createObjectURL(new Blob([buf], { type: "image/png" }));
        setSrc(objectUrl);
      })
      .catch(() => {
        setImgError(true);
      });

    return () => {
      // Revoke the object URL on cleanup to avoid memory leaks.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [area.id]);

  // Only render markers for assets that have both coordinates.
  const placed = assets.filter(
    (a) => a.plot_x_m != null && a.plot_y_m != null,
  );

  return (
    <div
      className="relative w-full h-full flex items-center justify-center p-4"
      style={{ overflow: "hidden" }}
    >
      {/* Aspect-ratio box that contains both the image and the marker layer. */}
      <div
        className="relative"
        style={{
          aspectRatio: w > 0 && h > 0 ? `${w} / ${h}` : undefined,
          maxWidth: "100%",
          maxHeight: "100%",
          width: "100%",
        }}
      >
        {/* Plot-plan schematic background */}
        {src && (
          <img
            src={src}
            alt={`${area.name} plot plan`}
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
          />
        )}
        {imgError && (
          <div
            className="absolute inset-0 flex items-center justify-center font-mono text-xs"
            style={{ color: "var(--ink-3)", background: "var(--paper-2)" }}
          >
            Schematic unavailable
          </div>
        )}

        {/* Asset markers — only render when we have valid dimensions to position them. */}
        {w > 0 &&
          h > 0 &&
          placed.map((a) => {
            const { leftPct, topPct } = toPlotPct(
              Number(a.plot_x_m),
              Number(a.plot_y_m),
              w,
              h,
            );
            const col = healthColor(a.worst_cml_status || "");
            return (
              <button
                key={a.id}
                onClick={() => onPick(a.id)}
                title={`${a.tag} · ${a.worst_cml_status || "no data"}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 font-mono flex items-center gap-0.5 hover:z-10"
                style={{ left: `${leftPct}%`, top: `${topPct}%`, fontSize: 9 }}
              >
                {/* Colour-coded square dot (2 visual channels: colour + shape) */}
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: "#fff",
                    border: `2px solid ${col}`,
                    flexShrink: 0,
                  }}
                />
                {/* Tag label as third channel (text) */}
                <span
                  style={{
                    color: "var(--ink-1)",
                    textShadow: "0 1px 2px #fff, 0 -1px 2px #fff, 1px 0 2px #fff, -1px 0 2px #fff",
                    marginLeft: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {a.tag}
                </span>
              </button>
            );
          })}
      </div>
    </div>
  );
}
